/**
 * Creates a namespaced, TTL-aware localStorage cache.
 *
 * @param {string} storageKey  - The localStorage key to store all entries under.
 * @param {number} defaultTtl  - Default time-to-live in milliseconds.
 * @returns {{ get, set, clear }}
 */
const createCache = (storageKey, defaultTtl = 24 * 60 * 60 * 1000) => {
  const load = () => JSON.parse(localStorage.getItem(storageKey)) ?? {};
  const save = (data) => localStorage.setItem(storageKey, JSON.stringify(data));

  const get = (id) => {
    const store = load();
    const entry = store[id];
    if (!entry) return null;
    if (Date.now() > entry.expiry) return null;
    return entry.value;
  };

  const set = (id, value, ttl = defaultTtl) => {
    const store = load();
    store[id] = { value, expiry: Date.now() + ttl };
    save(store);
  };

  const clear = (id) => {
    const store = load();
    if (id) {
      delete store[id];
    } else {
      save({});
      return;
    }
    save(store);
  };

  return { get, set, clear };
};

export default createCache;